import { useEffect, useState } from "react";
import api from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Loader2, X as XIcon } from "lucide-react";

const EMPTY = { name: "", client: "", location: "", status: "upcoming", start_date: "", end_date: "", notes: "", assignees: [], crew_size: 0 };

export default function ProjectFormDialog({ open, onOpenChange, project, onSubmit }) {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [users, setUsers] = useState([]);
  const isEdit = !!project;

  useEffect(() => {
    if (open) {
      const initial = project ? { ...EMPTY, ...project } : EMPTY;
      if (initial.start_date && initial.start_date.length > 10) initial.start_date = initial.start_date.slice(0, 10);
      if (initial.end_date && initial.end_date.length > 10) initial.end_date = initial.end_date.slice(0, 10);
      if (!Array.isArray(initial.assignees)) initial.assignees = [];
      initial.crew_size = initial.crew_size || 0;
      setForm(initial);
      api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
    }
  }, [open, project]);

  const update = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await onSubmit(form);
      onOpenChange(false);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg rounded-md" data-testid="project-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-black tracking-tight">
            {isEdit ? "Edit Project" : "New Project"}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label className="text-xs font-bold uppercase tracking-widest text-slate-500">Project Name</Label>
            <Input
              required
              value={form.name}
              onChange={update("name")}
              data-testid="project-name-input"
              className="mt-1.5 rounded-sm"
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs font-bold uppercase tracking-widest text-slate-500">Client</Label>
              <Input
                value={form.client}
                onChange={update("client")}
                data-testid="project-client-input"
                className="mt-1.5 rounded-sm"
              />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-widest text-slate-500">Location</Label>
              <Input
                value={form.location}
                onChange={update("location")}
                data-testid="project-location-input"
                className="mt-1.5 rounded-sm"
              />
            </div>
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-widest text-slate-500">Status</Label>
            <Select value={form.status} onValueChange={update("status")}>
              <SelectTrigger className="mt-1.5 rounded-sm" data-testid="project-status-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="upcoming">Upcoming</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs font-bold uppercase tracking-widest text-slate-500">Start Date</Label>
              <Input
                type="date"
                value={form.start_date || ""}
                onChange={update("start_date")}
                data-testid="project-start-input"
                className="mt-1.5 rounded-sm"
              />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-widest text-slate-500">End Date</Label>
              <Input
                type="date"
                value={form.end_date || ""}
                onChange={update("end_date")}
                data-testid="project-end-input"
                className="mt-1.5 rounded-sm"
              />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-2">
              <Label className="text-xs font-bold uppercase tracking-widest text-slate-500">Assign Team</Label>
              <div className="mt-1.5 flex flex-wrap gap-1.5 min-h-[42px] p-2 border border-slate-300 rounded-sm bg-white">
                {form.assignees.length === 0 && (
                  <span className="text-xs text-slate-400 self-center">No teammates assigned yet</span>
                )}
                {form.assignees.map((uid) => {
                  const u = users.find((x) => x.id === uid);
                  return (
                    <span key={uid} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm bg-blue-100 text-blue-800 text-xs font-semibold">
                      {u ? u.name : uid}
                      <button
                        type="button"
                        onClick={() => setForm((f) => ({ ...f, assignees: f.assignees.filter((x) => x !== uid) }))}
                        className="hover:text-blue-950"
                        aria-label="Remove"
                        data-testid={`unassign-${uid}`}
                      >
                        <XIcon size={12} />
                      </button>
                    </span>
                  );
                })}
              </div>
              <Select
                value=""
                onValueChange={(v) => v && setForm((f) => ({ ...f, assignees: [...new Set([...(f.assignees || []), v])] }))}
              >
                <SelectTrigger className="mt-2 rounded-sm" data-testid="assign-user-select">
                  <SelectValue placeholder="Add teammate…" />
                </SelectTrigger>
                <SelectContent>
                  {users.filter((u) => !form.assignees.includes(u.id)).map((u) => (
                    <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-widest text-slate-500">Crew Size</Label>
              <Input
                type="number"
                min="0"
                value={form.crew_size}
                onChange={(e) => setForm((f) => ({ ...f, crew_size: Number(e.target.value) || 0 }))}
                data-testid="project-crew-size-input"
                className="mt-1.5 rounded-sm font-mono"
              />
              <p className="mt-1 text-[11px] text-slate-500">Total people needed on this job</p>
            </div>
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-widest text-slate-500">Notes</Label>
            <Textarea
              value={form.notes}
              onChange={update("notes")}
              data-testid="project-notes-input"
              className="mt-1.5 rounded-sm"
              rows={3}
            />
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="rounded-sm"
              data-testid="project-form-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={busy}
              data-testid="project-form-submit-btn"
              className="bg-blue-800 hover:bg-blue-900 text-white rounded-sm"
            >
              {busy && <Loader2 size={16} className="animate-spin mr-1.5" />}
              {isEdit ? "Save Changes" : "Create Project"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
